<script setup lang="ts">
/**
 * Text attributes of the selected block reference (e.g. DIRECTION = "Bleu").
 *
 * Photo markers keep their own dedicated input (PhotoMarkerValueInput): this panel
 * covers every other block, with one field per attribute tag.
 *
 * Tags come from the customer's DWG and are shown as authored, never translated.
 */
import { ref, watch, onMounted, onBeforeUnmount } from "vue";
import { Facade } from "@/drawing";
import type { BlockAttributeInfo } from "@/drawing/blocks/attributes";
import { Logger } from "@/logger";
import PhosphorIcon from "@/components/UI/PhosphorIcon.vue";
import CanvasButton from "../CanvasButton.vue";
import FloatingPanel from "../FloatingPanel.vue";
import BlockBadgeIcon from "../BlockBadgeIcon.vue";

const props = defineProps<{
  target: string;
}>();

const attributes = ref<BlockAttributeInfo[]>([]);
/** Edited values, keyed by tag — committed on Enter or on the check button. */
const drafts = ref<Record<string, string>>({});
const isOpen = ref(false);

function refresh(): void {
  if (!Facade.isInitialized) {
    attributes.value = [];
    drafts.value = {};
    return;
  }
  attributes.value = Facade.blocks.getSelectedAttributes();
  drafts.value = Object.fromEntries(
    attributes.value.map((attr) => [attr.tag, attr.value]),
  );
}

// The selection changes the target; undo/redo changes values without it.
watch(() => props.target, refresh, { immediate: true });

onMounted(() => {
  if (Facade.isInitialized) Facade.history.onChange(refresh);
});

onBeforeUnmount(() => {
  if (Facade.isInitialized) Facade.history.offChange(refresh);
});

function toggle(): void {
  isOpen.value = !isOpen.value;
  if (isOpen.value) refresh();
}

function commit(tag: string): void {
  if (!Facade.isInitialized) return;
  const value = drafts.value[tag] ?? "";
  Logger.info(`BlockAttributesPanel : ${tag} = ${value}`);
  Facade.blocks.setSelectedAttribute(tag, value);
  refresh();
}
</script>

<template>
  <div>
    <CanvasButton
      class="canvas-btn-wide"
      :active="isOpen"
      :tooltipText="$t('canvas:floating.attributes.tooltip.text')"
      :tooltipTitle="$t('canvas:floating.attributes.tooltip.title')"
      @click="toggle"
    >
      <BlockBadgeIcon badge="text-aa" badge-color="#4dabf7" :badge-size="16" />
    </CanvasButton>

    <FloatingPanel
      panel-id="block-attributes"
      :title="$t('canvas:floating.attributes.header')"
      :open="isOpen"
      @close="isOpen = false"
    >
      <p v-if="!attributes.length" class="empty-hint">
        {{ $t("canvas:floating.attributes.empty") }}
      </p>

      <div v-for="attribute in attributes" :key="attribute.tag" class="field">
        <label :for="`attr-${attribute.tag}`" class="field-label">
          {{ attribute.tag }}
        </label>
        <div class="field-input">
          <input
            :id="`attr-${attribute.tag}`"
            v-model="drafts[attribute.tag]"
            type="text"
            @keyup.enter="() => commit(attribute.tag)"
          />
          <button
            type="button"
            :title="$t('confirm')"
            @click="() => commit(attribute.tag)"
          >
            <PhosphorIcon name="check" :size="13" />
          </button>
        </div>
      </div>
    </FloatingPanel>
  </div>
</template>

<style lang="scss" scoped>
.empty-hint {
  margin: 0;
  padding: 0.25rem 0.75rem;
  color: rgba(255, 255, 255, 0.4);
  font-size: 0.8rem;
}

.field {
  padding: 6px 12px;

  & + .field {
    border-top: 1px solid rgba(255, 255, 255, 0.06);
  }
}

.field-label {
  display: block;
  margin-bottom: 3px;
  font-size: 0.75rem;
  color: rgba(255, 255, 255, 0.55);
  overflow-wrap: anywhere;
}

.field-input {
  display: flex;
  gap: 4px;

  input {
    flex: 1;
    min-width: 0;
    padding: 3px 6px;
    border-radius: 4px;
    border: 1px solid var(--canvas-border);
    background-color: rgba(255, 255, 255, 0.06);
    color: white;
    font-size: 0.8rem;

    &:focus {
      outline: none;
      border-color: var(--canvas-btn-active-bg);
      background-color: rgba(255, 255, 255, 0.1);
    }
  }

  button {
    flex: 0 0 auto;
    padding: 0 7px;
    border-radius: 4px;
    border: 1px solid var(--canvas-border);
    background: transparent;
    color: rgba(255, 255, 255, 0.7);

    &:hover {
      background-color: rgba(255, 255, 255, 0.08);
      color: white;
    }
  }
}
</style>
